import React, { useMemo, useState } from 'react'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import { saveAs } from 'file-saver'
import { DateTime } from 'luxon'
import { useSnackbar } from 'notistack'
import { useQuery } from '@apollo/client'
import FileCopyIcon from 'components/material-symbol/icons/FileCopy'
import FileDownloadOutlinedIcon from 'components/material-symbol/icons/FileDownload'
import { useLoadGroupEventFormValues } from 'components/group-events/create-group-event-form-wizard/hooks'
import { PageHeader } from 'components/page-header'
import SectionTitle from 'components/section-title'
import UpgradeModal from 'components/upgrade-modal'
import { useAuth } from 'lib/auth'
import {
  flags,
  useFeatureFlags,
  useFeatureFlagsLoading,
} from 'lib/feature-flags'
import {
  PGA_COACH_ACTION_BLUE,
  PGA_COACH_GROUP_EVENT_COLOR,
  PGA_COACH_PRIVATE_LESSON_COLOR,
} from 'pages/pga-coach/dashboard/dashboard-colors'
import { SCHEDULE_EVENT_TYPES } from 'pages/pga-coach/dashboard/todays-schedule/schedule-event-colors'
import { GET_COACH_ANALYTICS, GET_COACH_ANALYTICS_BENCHMARKS } from './query'
import {
  BookingMixCard,
  DownloadReportDialog,
  GroupEventDetails,
  LessonTypeDetails,
  MonthlyTrendChart,
  RankedListCard,
  StatCard,
} from './components'
import {
  formatCount,
  formatCompactCurrency,
  formatCurrency,
  formatEventDetail,
  formatFullTimeLabel,
  formatOptionalDecimal,
  formatOptionalPercent,
  formatPercent,
  getRollingWindow,
  hasTakeHome,
  sortByCountRevenueTitle,
  sumMonthlyCounts,
} from './utils'
import { getReportData } from './report-data'
import useStyles from './styles'

const Section = ({ title, children }) => {
  const classes = useStyles()

  return (
    <Box className={classes.section}>
      <SectionTitle size="large" className={classes.sectionTitle}>
        {title}
      </SectionTitle>
      {children}
    </Box>
  )
}

const CoachAnalytics = () => {
  const classes = useStyles()
  const { months, startDate, endDate, label: periodLabel } = useMemo(
    () => getRollingWindow(),
    [],
  )
  const [isCoachBenchmarksEnabled] = useFeatureFlags([
    flags.FLAG_FEAT_COACH_BENCHMARKS,
  ])
  const flagsLoading = useFeatureFlagsLoading()
  const variables = { timeRange: { startDate, endDate } }
  const { data, loading, error } = useQuery(GET_COACH_ANALYTICS, { variables })
  const {
    data: benchmarksData,
    loading: benchmarksLoading,
    error: benchmarksError,
  } = useQuery(GET_COACH_ANALYTICS_BENCHMARKS, {
    variables,
    skip: !isCoachBenchmarksEnabled,
    errorPolicy: 'all',
  })
  const { user } = useAuth()
  const { enqueueSnackbar } = useSnackbar()
  const loadGroupEventFormValues = useLoadGroupEventFormValues()
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false)
  const [isDownloadingReport, setIsDownloadingReport] = useState(false)
  const [lessonTypeDetailsId, setLessonTypeDetailsId] = useState(null)
  const [groupEventDetailsSlug, setGroupEventDetailsSlug] = useState(null)

  const analytics = data?.pgaCoachAnalytics
  const canDownloadReport = !user?.hideSensitiveInformation
  const benchmarks = benchmarksData?.pgaCoachAnalytics
  const isTakeHome = hasTakeHome(benchmarks)
  const queryState = { loading, error: Boolean(error) }
  const revenueState = {
    ...queryState,
    loading: loading || flagsLoading || benchmarksLoading,
  }
  const benchmarksState = {
    loading: benchmarksLoading,
    error: Boolean(benchmarksError),
  }

  const topLessonTypes = sortByCountRevenueTitle(
    analytics?.privateLessonTypes?.topBooked,
    {
      getCount: item => item.lessonCount,
      getRevenue: item => item.earnings,
      getTitle: item => item.lessonType.title,
    },
  ).map(item => ({
    id: item.lessonType.id,
    title: item.lessonType.title,
    onClick: item.lessonType.deletedAt
      ? undefined
      : () => setLessonTypeDetailsId(item.lessonType.id),
    detail: formatCurrency(item.earnings),
    caption: formatCount(item.lessonCount, 'booking'),
  }))
  const topGroupEvents = sortByCountRevenueTitle(
    analytics?.groupEvents?.topEarningEvents,
    {
      getCount: item => item.registrationCount,
      getRevenue: item => item.earnings,
      getTitle: item => item.groupEvent.title,
    },
  ).map(item => ({
    id: item.groupEvent.id,
    title: item.groupEvent.title,
    onClick: item.groupEvent.slug
      ? () => setGroupEventDetailsSlug(item.groupEvent.slug)
      : undefined,
    detail: formatEventDetail(item.groupEvent),
    caption: formatCount(item.registrationCount, 'registration'),
    action: {
      label: 'Duplicate event',
      icon: FileCopyIcon,
      onClick: () => loadGroupEventFormValues(item.groupEvent),
    },
  }))

  const downloadReport = async () => {
    setIsDownloadingReport(true)
    try {
      const { generateReportPdf } = await import('./generate-report-pdf')
      const blob = generateReportPdf({
        title: 'Coach Analytics',
        periodLabel,
        coachName: user?.name,
        generatedOn: DateTime.now().toFormat('LLL d, yyyy'),
        sections: getReportData({
          analytics,
          benchmarks,
          benchmarksError: Boolean(benchmarksError),
          months,
          topLessonTypes,
          topGroupEvents,
        }),
      })
      saveAs(blob, `coach-analytics-${startDate}-to-${endDate}.pdf`)
    } catch (downloadError) {
      window.rg4js &&
        window.rg4js('send', {
          error: downloadError,
          tags: ['coach-analytics-report'],
        })
      enqueueSnackbar('Failed to generate PDF', { variant: 'error' })
    } finally {
      setIsDownloadingReport(false)
      setIsReportDialogOpen(false)
    }
  }

  return (
    <UpgradeModal
      pageName="coach-analytics"
      title="Upgrade to Access Analytics"
      description="Get detailed insights into your coaching business with our premium analytics dashboard. Track earnings, leads, and performance metrics to grow your coaching practice."
    >
      <>
        <PageHeader
          title="Analytics"
          subtitle={periodLabel}
          actions={
            canDownloadReport && (
              <Button
                variant="outlined"
                color="primary"
                startIcon={<FileDownloadOutlinedIcon />}
                disabled={
                  loading ||
                  flagsLoading ||
                  benchmarksLoading ||
                  Boolean(error) ||
                  !analytics ||
                  !user?.coach?.hasPaidTierSubscription
                }
                onClick={() => setIsReportDialogOpen(true)}
              >
                Download report
              </Button>
            )
          }
        />
        <DownloadReportDialog
          open={isReportDialogOpen}
          onClose={() => setIsReportDialogOpen(false)}
          onDownload={downloadReport}
          isDownloading={isDownloadingReport}
          periodLabel={periodLabel}
        />
        <Box className={classes.container}>
          <Section title="Revenue">
            <Box className={classes.statGrid}>
              <StatCard
                label={
                  isTakeHome
                    ? 'Take-home through PGA Coach'
                    : 'Earnings through PGA Coach'
                }
                value={formatCurrency(analytics?.earnings?.total)}
                caption={periodLabel}
                {...revenueState}
              />
              <StatCard
                label={'Private lesson revenue'}
                value={formatCurrency(analytics?.privateLessonTypes?.revenue)}
                caption={periodLabel}
                {...revenueState}
              />
              <StatCard
                label={'Group event revenue'}
                value={formatCurrency(analytics?.groupEvents?.revenue)}
                caption={periodLabel}
                {...revenueState}
              />
            </Box>
            <MonthlyTrendChart
              id="takeHomeByMonth"
              className={classes.gridSpacing}
              title={isTakeHome ? 'Take-home by month' : 'Payments by month'}
              subtitle={periodLabel}
              data={analytics?.earnings?.byMonth}
              dataKey="total"
              months={months}
              color={PGA_COACH_ACTION_BLUE}
              valueFormatter={value =>
                isTakeHome
                  ? `${formatCurrency(value)} take-home`
                  : formatCurrency(value)
              }
              axisFormatter={formatCompactCurrency}
              summaryFormatter={formatCurrency}
              insightSubject={isTakeHome ? 'Take-home is' : 'Earnings are'}
              emptyMessage={
                isTakeHome ? 'No take-home recorded.' : 'No earnings recorded.'
              }
              errorMessage={`Unable to load ${
                isTakeHome ? 'take-home' : 'earnings'
              }. Please try again later.`}
              {...revenueState}
            />
          </Section>

          {isCoachBenchmarksEnabled && (isTakeHome || benchmarksError) && (
            <Section title="Performance benchmarks">
              <Box className={classes.statGrid}>
                <StatCard
                  label="Hours coached"
                  value={formatOptionalDecimal(benchmarks?.billableHours)}
                  caption={periodLabel}
                  {...benchmarksState}
                />
                <StatCard
                  label="Utilization"
                  value={formatOptionalPercent(benchmarks?.utilizationPercent)}
                  caption={periodLabel}
                  {...benchmarksState}
                />
                <StatCard
                  label={formatFullTimeLabel(benchmarks)}
                  value={formatOptionalPercent(
                    benchmarks?.fullTimeProgressPercent,
                  )}
                  caption="Last 12 months"
                  {...benchmarksState}
                />
              </Box>
            </Section>
          )}

          <Section title="Leads">
            <Box className={classes.statGrid}>
              <StatCard
                label="Total leads"
                value={(analytics?.leads?.totalCount || 0).toLocaleString()}
                caption={periodLabel}
                {...queryState}
              />
              <StatCard
                label={'Unviewed leads'}
                value={(analytics?.leads?.unviewedCount || 0).toLocaleString()}
                caption={periodLabel}
                {...queryState}
              />
              <StatCard
                label={'Conversion rate'}
                value={formatPercent(analytics?.leads?.conversionRate)}
                caption={periodLabel}
                {...queryState}
              />
            </Box>
            <MonthlyTrendChart
              id="leadsByMonth"
              className={classes.gridSpacing}
              title="Leads acquired by month"
              subtitle={periodLabel}
              data={analytics?.leads?.byMonth}
              dataKey="count"
              months={months}
              color={PGA_COACH_ACTION_BLUE}
              valueFormatter={value => formatCount(value, 'lead')}
              summaryFormatter={value => value.toLocaleString()}
              insightFormatter={value => formatCount(value, 'lead')}
              insightSubject="You've gained"
              emptyMessage="No leads."
              errorMessage="Unable to load leads. Please try again later."
              {...queryState}
            />
          </Section>

          <Section title="Booking mix">
            <Box className={classes.chartGrid}>
              <BookingMixCard
                subtitle={periodLabel}
                privateBookings={sumMonthlyCounts(
                  analytics?.privateLessonTypes?.byMonth,
                )}
                groupBookings={sumMonthlyCounts(
                  analytics?.groupEvents?.byMonth,
                )}
                privateRevenue={analytics?.privateLessonTypes?.revenue}
                groupRevenue={analytics?.groupEvents?.revenue}
                errorMessage="Unable to load booking mix. Please try again later."
                {...queryState}
              />
            </Box>
          </Section>

          <Section title="Private lessons">
            <Box className={classes.chartGrid}>
              <MonthlyTrendChart
                id="privateLessonsByMonth"
                title="Private lessons by month"
                subtitle={periodLabel}
                data={analytics?.privateLessonTypes?.byMonth}
                dataKey="count"
                months={months}
                color={PGA_COACH_PRIVATE_LESSON_COLOR}
                valueFormatter={value => formatCount(value, 'lesson')}
                summaryFormatter={value => value.toLocaleString()}
                insightFormatter={value => formatCount(value, 'lesson')}
                insightSubject="You've booked"
                emptyMessage="No private lessons."
                errorMessage="Unable to load private lessons. Please try again later."
                {...queryState}
              />
              <RankedListCard
                title="Top booked lesson types"
                subtitle={periodLabel}
                items={topLessonTypes}
                eventType={SCHEDULE_EVENT_TYPES.PRIVATE}
                emptyMessage="No lesson bookings."
                errorMessage="Unable to load lesson types. Please try again later."
                {...queryState}
              />
            </Box>
          </Section>

          <Section title="Group events">
            <Box className={classes.chartGrid}>
              <MonthlyTrendChart
                id="groupEventsByMonth"
                title="Enrollments by month"
                subtitle={periodLabel}
                data={analytics?.groupEvents?.byMonth}
                dataKey="count"
                months={months}
                color={PGA_COACH_GROUP_EVENT_COLOR}
                valueFormatter={value => formatCount(value, 'enrollment')}
                summaryFormatter={value => value.toLocaleString()}
                insightFormatter={value => formatCount(value, 'enrollment')}
                insightSubject="You've had"
                emptyMessage="No group event enrollments."
                errorMessage="Unable to load group events. Please try again later."
                {...queryState}
              />
              <RankedListCard
                title="Top group events"
                subtitle={periodLabel}
                items={topGroupEvents}
                eventType={SCHEDULE_EVENT_TYPES.GROUP_EVENT}
                emptyMessage="No group event registrations."
                errorMessage="Unable to load group events. Please try again later."
                {...queryState}
              />
            </Box>
          </Section>
        </Box>
        <LessonTypeDetails
          lessonTypeId={lessonTypeDetailsId}
          onClose={() => setLessonTypeDetailsId(null)}
        />
        <GroupEventDetails
          slug={groupEventDetailsSlug}
          onClose={() => setGroupEventDetailsSlug(null)}
        />
      </>
    </UpgradeModal>
  )
}

export default CoachAnalytics
